import type {
  FoundationLateralDatumAbstraction,
  FoundationVerticalDatumAbstraction,
} from "building-types/datums/datumAbstractionTypes";
import type { VerticalDatum, LateralDatum } from "building-types/datums/datumsTypes";
import type { FoundationDatumVerticalAnchor } from "building-types/datums/datumsTypesEnum";
import type { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { Drawing } from "building-types/drawings/drawingTypes";
import type { DrawingId } from "building-types/drawings/drawingTypesEnum";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { FormulaDisabled } from "building-types/options/formulaDisabled";
import type { Table } from "common/data/table";
import type { DocEntityHandle } from "../handles/docEntityHandleTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { FoundationAssemblyEntityDatums } from "./foundationAssemblyEntityDatumsTypes";
import { FoundationAssemblyType } from "./foundationAssemblyTypesEnum";
import type { FoundationComponentType, FoundationComponentBase } from "./foundationComponentEntityTypes";
import type {
  FoundationSubSystemType,
  FoundationCondition,
  FoundationSuperType,
  FoundationSubType,
} from "./foundationEnums";
import type { PierPosition } from "./foundationPatternTypes";

export interface FoundationAssemblyEntity extends DocEntityBase<"FOUNDATION_ASSEMBLY"> {
  readonly name: string;
  readonly id: PrimaryEntityId<"FOUNDATION_ASSEMBLY">;
  readonly assemblySystem: FoundationSubSystemType;
  readonly tag?: string;
  readonly footingId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly stemWallId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly curbId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly pierId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly lateralDatumId?: ForeignEntityId<"DATUM">;
  readonly topDatumId?: ForeignEntityId<"DATUM">;
  readonly bottomDatumId?: ForeignEntityId<"DATUM">;
  readonly brickledgeTopDatumId?: ForeignEntityId<"DATUM">;
}

export type FoundationAssemblyId = FoundationAssemblyEntity["id"];

export type FoundationAssemblyEntityTable = {
  [id: FoundationAssemblyId]: FoundationAssemblyEntity;
};

export interface FoundationAssemblyComponent<T extends FoundationComponentType>
  extends Omit<FoundationComponentBase<T>, "name"> {
  readonly componentId: ForeignEntityId<"FOUNDATION_COMPONENT"> | undefined;
}

export type FoundationDatumTable = Table<FoundationDatum>;

export type FoundationDatum = FoundationVerticalDatum | FoundationLateralDatum;

export interface FoundationVerticalDatum extends VerticalDatum<FoundationDatumVerticalAnchor> {
  readonly name: string;
}

export interface FoundationLateralDatum extends LateralDatum<WallLateralDatumAnchor> {
  readonly name: string;
}

export interface FoundationAssemblyEntities extends FoundationAssemblyEntityDatums {
  readonly assemblySystem: FoundationSubSystemType;
  readonly name: string;
  readonly tag?: string;
  readonly footingId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly stemWallId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly curbId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
  readonly pierId?: ForeignEntityId<"FOUNDATION_COMPONENT">;
}

interface FoundationAssemblyDetailInfo {
  readonly drawingNumber?: number;
  readonly drawingId?: DrawingId | ForeignEntityId<"DRAWING">;
}

interface FoundationAssemblyBase<T extends FoundationAssemblyType> {
  readonly assemblyType: T;
  readonly lateralDatumAbstraction: FoundationLateralDatumAbstraction;
  readonly topDatumAbstraction?: FoundationVerticalDatumAbstraction;
  readonly bottomDatumAbstraction?: FoundationVerticalDatumAbstraction;
}

export type FoundationAssembly = SolidFoundationAssembly | VoidFoundationAssembly;

export type VoidFoundationAssembly = FoundationAssemblyBase<FoundationAssemblyType.VOID>;

export type IsolatedFoundationPickSource =
  | {
      readonly kind: "edgeBehavior";
      readonly edgeBehaviorId: ForeignEntityId<"EDGE_BEHAVIOR">;
    }
  | {
      readonly kind: "sourceEntity";
      readonly sourcePickHandle: DocEntityHandle;
    };

export interface SolidFoundationAssembly
  extends FoundationAssemblyBase<FoundationAssemblyType.SOLID>,
    FoundationAssemblyDetailInfo {
  readonly assemblyId: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
  readonly stemWall?: FoundationAssemblyComponent<FoundationComponentType.STEMWALL>;
  readonly footing?: FoundationAssemblyComponent<FoundationComponentType.FOOTING>;
  readonly curb?: FoundationAssemblyComponent<FoundationComponentType.CURB>;
  readonly pier?: FoundationAssemblyComponent<FoundationComponentType.PIER>;

  readonly pierPositions?: PierPosition[];
  readonly pierOffset?: number[];
  readonly pierRotation?: number;

  readonly brickLedgeTopDatumAbstraction?: FoundationVerticalDatumAbstraction;

  readonly pickSource?: IsolatedFoundationPickSource;
}

export type FoundationConditionDataTable = { [K in FoundationCondition]: Partial<FoundationConditionData> };

export interface FoundationConditionData extends Pick<Drawing, "scale" | "scaleMode"> {
  readonly drawingNumber: number;
  readonly drawingId: DrawingId | ForeignEntityId<"DRAWING">;
  readonly assemblyId: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
}

export interface FoundationSystemAssemblyOverride<S extends FoundationSuperType, T extends FoundationSubType<S>>
  extends FoundationAssemblyDetailInfo,
    FoundationAssemblyEntityDatums,
    FormulaDisabled {
  readonly id: string;
  readonly name: string;
  readonly superSystem: S;
  readonly assemblySystem: T;
  readonly assemblyId?: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
}

export type FoundationAssemblyOverrideArray<
  S extends FoundationSuperType,
  T extends FoundationSubType<S>,
> = FoundationSystemAssemblyOverride<S, T>[];

export type FoundationAssemblyOverrides = FoundationAssemblyOverrideArray<
  FoundationSuperType,
  FoundationSubType<FoundationSuperType>
>;

export function isSolidFoundationAssembly(assembly: FoundationAssembly): assembly is SolidFoundationAssembly {
  return assembly.assemblyType === FoundationAssemblyType.SOLID;
}
